import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { Button } from '@olxbr/ds-core'
import { Container } from '@olxbr/ds-web'
import { EmailContainer } from '../../molecules/EmailContainer'
import { ConfirmPhoneOverrideModal } from '../ConfirmPhoneOverrideModal/ConfirmPhoneOverrideModal'
import { phoneOverride, phoneOverridev4 } from '../../../api/phoneValidationApiCall'
import { breakpoints } from '../../../util/medias'
import Lurker from '../../../util/lurker'
import { View } from '../../atoms/View'
import { MANDATORY_TIPIFICATION_KEY } from '../../../util/global.consts'
import storage from '../../../util/storage'

const getContentElement = ({ modalMode, ...props }) =>
  modalMode ? <View {...props} /> : <Container {...props} />

const lurker = new Lurker('phone_authorization_confirmation')

const ButtonsWrapper = styled.div`
  margin-top: var(--spacing-3);
  display: flex;
  justify-content: end;
  @media screen and (max-width: ${breakpoints['mobile']}px) {
    justify-content: center;
    flex-direction: column-reverse;
    align-items: center;
    flex: 1;
  }
`

const StyledButton = styled(Button)`
  @media screen and (max-width: ${breakpoints['mobile']}px) {
    margin: 0;
    width: 100%;
    margin-bottom: var(--spacing-2);
  }

  ${({ modalMode }) =>
    modalMode &&
    `
    width: 100%;
    margin-bottom: var(--spacing-2);
  `}
`

const StyledText = styled.span.attrs({ className: 'typo-body-medium' })`
  @media screen and (max-width: ${breakpoints['mobile']}px) {
    font-size: var(--spacing-8);
    font-size: 16px;
  }
`

const ContainerWrapper = styled(getContentElement)`
  padding: var(--spacing-8);
  @media (max-width: ${breakpoints['mobile']}px) {
    border: none !important;
    padding: 0;
    font-size: 16px;
  }

  ${({ modalMode }) => modalMode && `padding: var(--spacing-1);`}
`

export const RemovePhoneConfirmationTemplate = ({
  phone,
  token,
  pincode,
  emails,
  onSuccess,
  onCancel,
  abTests,
  modalMode = false,
  onAccountCompleteData,
}) => {
  const [modalOpen, setModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({ showGenericError: false })

  const onOverrideClick = e => {
    e.preventDefault()

    if (!modalMode) {
      setModalOpen(true)
    } else {
      navigateToConfirmPhoneOverridePage()
    }
  }

  const closeModal = e => {
    e.preventDefault()
    setModalOpen(false)
  }

  const onPhoneOverrideSuccess = ({ data }) => {
    if (data) {
      const { publicAccountId } = data
      lurker.setPublicAccountId(publicAccountId)

      lurker.viewMessageEvt('success')
    }

    if (typeof onSuccess === 'function') {
      onSuccess()
    }
  }

  const showError = error => setErrors({ ...errors, [error]: true })

  const onGenericError = (error = {}, eventName = 'error') => {
    const { status, response } = error
    const errorMessage = response?.data?.message

    if (errorMessage === 'ACCOUNT_TYPING_REQUIRED') {
      lurker.viewMessageEvt('error', {
        accounts_reason: errorMessage?.toLowerCase(),
        accounts_status_code: status,
        login_type: 'phone_override',
      })

      storage.set(MANDATORY_TIPIFICATION_KEY, true)

      if (onAccountCompleteData) {
        onAccountCompleteData(response?.data?.token)
      }
    } else {
      const genericError = 'showGenericError'
      lurker.viewMessageEvt(eventName, {
        accounts_reason: 'unexpected_error',
        accounts_error_message: (error && error?.message) || 'unknown',
      })
      showError(genericError)
    }
  }

  const onPhoneOverrideClick = () => {
    setLoading(true)
    const phoneOverrideCall = token
      ? (phone, pincode) => phoneOverridev4(phone, pincode, token, abTests)
      : phoneOverride
    phoneOverrideCall(phone, pincode)
      .then(onPhoneOverrideSuccess)
      .catch(err => onGenericError(err, 'error'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    lurker.pageLoadEvt()
  }, [])

  return (
    <React.Fragment>
      <ContainerWrapper modalMode={modalMode}>
        <div className="flex flex-col">
          {emails.map(email => (
            <EmailContainer key={email} email={email} />
          ))}
          <StyledText className="mt-3">
            Só é permitido um telefone vinculado a uma conta. Ao continuar a verificação, ele será
            desvinculado dessas contas e os anúncios nelas serão deletados.
          </StyledText>
        </div>
      </ContainerWrapper>
      <ButtonsWrapper>
        <StyledButton
          modalMode={modalMode}
          onClick={onCancel}
          variant="link"
          size="medium"
          className="mr-2"
        >
          Usar outro número
        </StyledButton>
        <StyledButton modalMode={modalMode} size="medium" type="button" onClick={onOverrideClick}>
          Continuar verificação e desvínculo
        </StyledButton>
      </ButtonsWrapper>
      <ConfirmPhoneOverrideModal
        loading={loading}
        isOpen={modalOpen}
        emails={emails}
        onConfirm={onPhoneOverrideClick}
        closeModal={closeModal}
        {...errors}
      />
    </React.Fragment>
  )
}

RemovePhoneConfirmationTemplate.propTypes = {
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  phone: PropTypes.string,
  emails: PropTypes.arrayOf(PropTypes.string),
  abTests: PropTypes.string,
  token: PropTypes.string,
  modalMode: PropTypes.bool,
  onAccountCompleteData: PropTypes.func,
}
